import { useState } from "react";
import {
  useDeleteAdMutation,
  useFetchAdsQuery,
  useToggleAdIsActiveStatusMutation,
} from "../store";
import Pagination from "../components/common/Pagination";
import ToggleSwitch from "../components/common/ToggleSwitch";
import NavigationLinkBtn from "../components/common/NavigationLinkBtn";
import { FaEdit, FaRegTrashAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import { Ad } from "../types";
import { showToast, ToastType } from "../utils/toast.utils";
import Modal from "../components/common/Modal";
import Button from "../components/common/Button";

const Ads: React.FC = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [adToDelete, setAdToDelete] = useState<Ad | null>(null);

  const [deleteAd] = useDeleteAdMutation();
  const [toggleActive] = useToggleAdIsActiveStatusMutation();

  const confirmDelete = (adToDelete: Ad) => {
    setAdToDelete(adToDelete);
    setShowConfirmModal(true);
  };

  const handleDeleteAd = async () => {
    if (!adToDelete) return;
    const result = await deleteAd(adToDelete._id);
    if (!result.error) {
      showToast("Bannière supprimée", ToastType.SUCCESS);
    }
    setShowConfirmModal(false);
    setAdToDelete(null);
  };

  const { data: ads } = useFetchAdsQuery({
    page,
    perPage,
  });

  return (
    <div className="p-6">
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Bannières Publicitaires
          </h2>
          <NavigationLinkBtn
            to="/ads/create"
            label="Créer une bannière"
          />
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Aperçu
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Villes
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Créée le
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ads?.data?.map((row) => (
                <tr
                  key={row._id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-16 w-24 rounded overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
                      <img
                        src={row.imagePath}
                        alt="bannière"
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {row.cities.map((city) => (
                        <span
                          key={city._id}
                          className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 border border-gray-200"
                        >
                          {city.displayName}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex justify-center">
                      <ToggleSwitch
                        isOn={row.isActive}
                        onToggle={() => {
                          toggleActive({
                            adId: row._id,
                            isActive: !row.isActive,
                          });
                        }}
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {row.createdAt}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={row._id}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                        title="Modifier"
                      >
                        <FaEdit className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => confirmDelete(row)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                        title="Supprimer"
                      >
                        <FaRegTrashAlt className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="border-t border-gray-200 p-4">
          <Pagination
            paginationInfo={{
              page,
              perPage,
              count: ads?.paginationInfo.count || 1,
            }}
            setPage={setPage}
            setPerPage={setPerPage}
          />
        </div>
      </div>

      {showConfirmModal && (
        <Modal
          onClose={() => setShowConfirmModal(false)}
          actionBar={
            <>
              <Button
                className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-4 py-2 rounded mr-2"
                onClick={() => setShowConfirmModal(false)}
              >
                Annuler
              </Button>
              <Button
                className="bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded"
                onClick={handleDeleteAd}
              >
                Supprimer
              </Button>
            </>
          }
        >
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Confirmer la suppression
            </h3>
            <p className="text-sm text-gray-500">
              Êtes-vous sûr de vouloir supprimer cette bannière publicitaire ?
              Cette action est irréversible.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Ads;
